/**
 * 文件服务工厂
 *
 * 为文件上传/下载/删除生成标准化的服务方法。
 *
 */

import type { ApiResponse } from '../types/api';

// ===== 类型定义 =====

/**
 * 上传参数
 */
export interface UploadParams {
  /** 文件 */
  file: File;
  /** 文件模块/业务类型 */
  moduleType?: string;
  /** 业务 ID */
  businessId?: string | number;
  /** 文件类型标识 */
  fileType?: string;
  /** 自定义路径 */
  customPath?: string;
  /** 额外参数 */
  extra?: Record<string, string | number>;
}

/**
 * 上传结果
 */
export interface UploadResult {
  /** 文件访问 URL */
  url: string;
  /** 文件名 */
  name?: string;
  /** 文件大小 (bytes) */
  size?: number;
  /** 文件类型 */
  type?: string;
  /** 服务器返回的文件 ID */
  fileId?: string | number;
  /** 原始响应数据 */
  [key: string]: unknown;
}

/**
 * 文件服务配置
 */
export interface FileServiceConfig {
  /**
   * request 函数，来自 @umijs/max
   */
  request: <R>(url: string, options?: Record<string, unknown>) => Promise<R>;

  /** 上传配置 */
  upload?: {
    /** 上传路径, 默认 '/upload' */
    path?: string;
    /** HTTP 方法, 默认 'POST' */
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    /** 额外请求配置 (如 skipEncrypt: true) */
    requestOptions?: Record<string, unknown>;
  };

  /** 删除配置 */
  delete?: {
    /** 删除路径, 默认 '/' */
    path?: string;
    /** HTTP 方法, 默认 'DELETE' */
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    /** 删除参数名 (放在 query 中), 默认 'fileUrl' */
    paramName?: string;
    /** 额外请求配置 (如 skipEncrypt: true) */
    requestOptions?: Record<string, unknown>;
  };

  /** 下载配置 */
  download?: {
    /** 下载路径 (支持 :id 占位符), 默认 '/download/:id' */
    path?: string;
    /** HTTP 方法, 默认 'GET' */
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    /** 额外请求配置 (如 headers) */
    requestOptions?: Record<string, unknown>;
  };
}

/**
 * 文件服务方法
 */
export interface FileServiceMethods {
  /** 上传文件 */
  upload: (params: UploadParams) => Promise<ApiResponse<UploadResult>>;
  /** 删除文件 */
  remove: (fileUrl: string) => Promise<ApiResponse<unknown>>;
  /** 下载文件 (返回 Blob) */
  download: (fileId: string | number) => Promise<Blob>;
}

// ===== 工厂函数 =====

/**
 * 创建文件服务
 *
 * @param basePath - 文件 API 基础路径, 如 '/api/files'
 * @param config - 配置项
 * @returns 文件服务方法
 *
 * @example
 * ```ts
 * import { request } from '@umijs/max';
 *
 * const fileService = createFileService('/api/files', {
 *   request,
 *   upload: { path: '/upload', requestOptions: { skipEncrypt: true } },
 *   delete: { path: '/', paramName: 'fileUrl' },
 *   download: { path: '/download/:id' },
 * });
 *
 * // 上传
 * const res = await fileService.upload({
 *   file: selectedFile,
 *   moduleType: 'avatar',
 *   businessId: userId,
 * });
 *
 * // 删除
 * await fileService.remove('https://cdn.example.com/files/abc.jpg');
 *
 * // 下载
 * const blob = await fileService.download('file-123');
 * ```
 */
export function createFileService(
  basePath: string,
  config: FileServiceConfig,
): FileServiceMethods {
  const { request: requestFn } = config;

  // --- upload ---
  const uploadCfg = config.upload ?? {};
  const upload = async (
    params: UploadParams,
  ): Promise<ApiResponse<UploadResult>> => {
    const formData = new FormData();
    formData.append('file', params.file);

    if (params.moduleType) formData.append('moduleType', params.moduleType);
    if (params.businessId != null)
      formData.append('businessId', String(params.businessId));
    if (params.fileType) formData.append('fileType', params.fileType);
    if (params.customPath) formData.append('customPath', params.customPath);

    if (params.extra) {
      for (const [key, val] of Object.entries(params.extra)) {
        formData.append(key, String(val));
      }
    }

    const url = `${basePath}${uploadCfg.path ?? '/upload'}`;
    return requestFn<ApiResponse<UploadResult>>(url, {
      method: uploadCfg.method ?? 'POST',
      requestType: 'form',
      data: formData,
      ...uploadCfg.requestOptions,
    });
  };

  // --- delete ---
  const deleteCfg = config.delete ?? {};
  const remove = async (fileUrl: string): Promise<ApiResponse<unknown>> => {
    const url = `${basePath}${deleteCfg.path ?? '/'}`;
    const paramName = deleteCfg.paramName ?? 'fileUrl';
    return requestFn<ApiResponse<unknown>>(url, {
      method: deleteCfg.method ?? 'DELETE',
      params: { [paramName]: fileUrl },
      ...deleteCfg.requestOptions,
    });
  };

  // --- download ---
  const downloadCfg = config.download ?? {};
  const download = async (fileId: string | number): Promise<Blob> => {
    const pathTemplate = downloadCfg.path ?? '/download/:id';
    const url = `${basePath}${pathTemplate.replace(':id', String(fileId))}`;
    return requestFn<Blob>(url, {
      method: downloadCfg.method ?? 'GET',
      responseType: 'blob',
      ...downloadCfg.requestOptions,
    });
  };

  return { upload, remove, download };
}
